import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Users, Music, Star } from 'lucide-react';

const HERO_IMAGE = "https://images.unsplash.com/photo-1756978303719-57095d8bd250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMGZlc3RpdmFsJTIwY29uY2VydCUyMGNyb3dkfGVufDB8fHx8MTc3NDUzNjkwMnww&ixlib=rb-4.1.0&q=85";

const HeroSection = () => {
  const stats = [
    { icon: Calendar, value: '115K+', label: 'Event Views' },
    { icon: Music, value: '50+', label: 'Artists' },
    { icon: Star, value: '10+', label: 'Sponsors' },
  ];

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Purple Overlay */}
      <div className="absolute inset-0">
        <img 
          src={HERO_IMAGE}
          alt="Baltimore Music Scene"
          className="w-full h-full object-cover"
        />
        {/* Purple gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-purple-800/60 to-black" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 py-32 pt-40">
        <div className="max-w-3xl">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-purple-300 font-semibold text-lg mb-4"
          >
            Discover Baltimore
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
          >
            Get Ready to Experience the Best of{' '}
            <span className="text-purple-400">Baltimore's</span> Arts & Culture
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-300 text-lg sm:text-xl leading-relaxed mb-8"
          >
            Whether you're looking for things to do this weekend, this month, or next year — 
            we don't want you to miss a thing. Check out some of our favorite local events!
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4"
          >
            <a 
              href="https://events.whatshappeningbaltimore.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-explore-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 flex items-center gap-2 group"
            >
              Explore Events
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="https://events.sanitylounge.com/selection"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-submit-btn"
              className="border border-white/30 hover:border-purple-400 text-white hover:text-purple-400 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300"
            >
              Submit Your Event
            </a>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
        >
          {stats.map((stat, index) => (
            <div 
              key={stat.label}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6 text-center"
            >
              <stat.icon className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <p className="text-3xl font-bold text-white font-outfit">{stat.value}</p>
              <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
